import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { translateCountryName } from '@/app/common/countryNames'
import { useQueryCountries } from '@/modules/shared/query-client/useQueryCountries'
import { CountryModel } from '@/modules/shared/types/country'
import type { Moment } from 'moment'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { AllowanceModel } from '@/app/models/newDelegation/allowanceModel'
import { AccomodationModel } from '@/app/models/newDelegation/accomodationModel'
import { InvoiceModel } from '@/app/models/newDelegation/invoiceModel'
import { AdvancePaymentModel } from '@/app/models/newDelegation/advancePaymentModel'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import { useTripSaveIssues } from '../hooks/useTripSaveIssues'

interface EditTripData {
  companyId: string
  id: string
  driverId: string
  exchangeRateDate: Moment
  calculationDate: Moment
  settlementMonth: Moment
  country: CountryModel
  number: string
  purpose: string
  isSettled: boolean
  doNotConvertCurrencies: boolean
  events: Array<EventModel>
  allowances: Array<AllowanceModel>
  accomodations: Array<AccomodationModel>
  invoices: Array<InvoiceModel>
  advancePayments: Array<AdvancePaymentModel>
  alimentations: Array<AllowanceModel>
  isAccomodationModifiedByUser: boolean
  isDestinationCountryModifiedByUser: boolean
  force: boolean
  forceWithoutExchangeRates: boolean
  withPreviousExchangeRateDate: boolean
  forceCopyToCoDriver: boolean
  copyToCoDriver: boolean
  checkedTripCoDrivers: Array<boolean>
}
interface EditBusinnessTripFromContextType {
  data: EditTripData
  setData: React.Dispatch<React.SetStateAction<EditTripData>>
  countriesOptions: {
    label: string
    value: string
  }[]
  countries: any
  loading: boolean
  handleSubmit: () => Promise<void>
}

const EditBusinessTripFormContext =
  React.createContext<EditBusinnessTripFromContextType | null>(null)
export const EditBusinessTripFormProvider = ({
  children,
  companyId,
  formData,
  tripId,
  driverId,
  refreshData,
}: {
  children: React.ReactNode
  companyId: string
  formData: EditTripData
  tripId: string
  driverId: string
  refreshData: () => void
}) => {
  const { activeLanguage } = useLocalize()
  const lang = activeLanguage?.code || 'pl'
  const [data, setData] = React.useState<EditTripData>(formData)
  const [loading, setLoading] = React.useState(false)
  const { getExchangeRate } = useBusinessTripService()
  const { saveTrip } = useTripSaveIssues()

  const { data: countries } = useQueryCountries({
    companyId: companyId,
  })
  const recalcAllowanceRate = async (item: AllowanceModel) => {
    const code = item.amountCurrency?.code
    if (!code || code.toUpperCase() === 'PLN') return item
    try {
      const rate = await getExchangeRate({ companyId, exchangeRateDate: data.exchangeRateDate, currencyCode: code })
      if (rate != null) return { ...item, exchangeRate: rate.rate, table: rate.table, plnAmount: item.amount * rate.rate }
    } catch (err) {
      console.error('Error fetching exchange rate for allowance:', err)
    }
    return item
  }

  const handleSubmit = async () => {
    setLoading(true)
    try {
      let dataToSave = data
      if (data.doNotConvertCurrencies && data.exchangeRateDate) {
        const [allowances, alimentations] = await Promise.all([
          Promise.all((data.allowances ?? []).map(recalcAllowanceRate)),
          Promise.all((data.alimentations ?? []).map(recalcAllowanceRate)),
        ])
        dataToSave = { ...data, allowances, alimentations }
      }
      // In WPF these settings are fields of the trip editor, so changing them saves
      // through TripViewModel.OnSave() — with the same validation and Yes/No dialogs
      // as the Save button. Share that handling instead of bypassing it.
      await saveTrip({
        companyId,
        tripId,
        data: { companyId, driverId, ...dataToSave },
      })
    } catch (error) {
      console.error('Error updating business trip:', error)
    } finally {
      setLoading(false)
      refreshData()
    }
  }
  const countriesOptions = React.useMemo(() => {
    return Array.isArray(countries)
      ? countries.map((c) => ({
          label: translateCountryName(c.name, lang),
          value: c.id.toString(),
        }))
      : []
  }, [countries, lang])

  const value = React.useMemo(
    () => ({
      handleSubmit,
      countriesOptions,
      setData,
      data,
      countries,
      loading,
    }),
    [countriesOptions, data, countries, loading],
  )

  return (
    <EditBusinessTripFormContext.Provider value={value}>
      {children}
    </EditBusinessTripFormContext.Provider>
  )
}

export const useEditBusinessTripForm = () => {
  const ctx = React.useContext(EditBusinessTripFormContext)
  if (!ctx)
    throw new Error(
      'useEditBusinessTripForm must be used within EditBusinessTripFormProvider',
    )
  return ctx
}
